'use client';

// npm packages
import React, { useEffect, useRef, useState } from 'react';
import useWindowDimensions from '@/app/hooks/useWindowSize';
import { screenTypes } from '@/constants/types';
import { cn } from '@/utils/cn';
import { SWIPE } from '@/constants';

// components
import Text from '../Text';
import Icon from '../Icon';
import Button from '../Button';

function EMIDetailsModal({ onView, onClose, children, title, cardRef }) {
  const modalRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [cardRect, setCardRect] = useState('');
  const windowDimensions = useWindowDimensions();
  const [translateY, setTranslateY] = useState(0);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    setOpenModal(onView);

    // Check if the window is small on mount
    const checkMobileView = () => {
      setIsMobile(windowDimensions.width < screenTypes.large);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);

    return () => {
      window.removeEventListener('resize', checkMobileView);
    };
  }, [onView]);

  useEffect(() => {
    if (openModal) {
      if (!isMobile) {
        document.body.style.overflow = '';
      }
    }

    if (openModal && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [openModal, isMobile, cardRef]);

  // Close modal on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        !cardRef.current.contains(event.target)
      ) {
        onClose(false);
      }
    };

    if (openModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openModal, onClose]);

  const handleClose = (state) => {
    if (state === SWIPE) {
      setIsClosing(true);
      setTimeout(() => {
        onClose(false);
        setTranslateY(0);
        setIsClosing(false);
      }, 200);
    } else {
      onClose(false);
    }
  };

  const handleTranslateY = (value) => {
    setTranslateY(value);
  };

  if (!openModal) return null;

  return (
    <div
      role="presentation"
      className={cn(
        'fixed invisible lg:static inset-0 bg-black/50 lg:bg-transparent transition-opacity duration-300',
        {
          '!visible opacity-100 z-[100]': openModal,
        },
      )}
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        style={{
          transform: isMobile ? `translateY(${translateY}px)` : undefined,
          transition: isClosing
            ? 'transform 0.3s ease-out, opacity 0.3s ease-out'
            : 'transform 0.1s ease-out',
          opacity: isClosing ? 0 : 1,
        }}
        className={cn(
          'bg-white w-full rounded-t-xl lg:rounded-xl shadow-emi-details-modal p-[20px] transition-transform duration-300 ease-out invisible fixed bottom-full lg:absolute lg:bottom-auto lg:top-[102%] left-0 z-[120]',
          {
            '!bottom-0 lg:!bottom-auto !visible !opacity-100': openModal,
          },
        )}
      >
        <Button
          type="modal-swipe-close-button"
          onTranslateY={handleTranslateY}
          onClose={handleClose}
        />

        <div className="flex justify-between items-center sm:mb-[20px] mb-[16px]">
          <Text
            type="section_sub_title"
            className="text-coarse-wool !text-[18px] font-bold leading-20 sm:leading-28 tracking-[0.28px] sm:tracking-[0.36px]"
          >
            {title}
          </Text>
          <Icon
            type="close"
            color="#49525E"
            className="cursor-pointer min-w-[24px]"
            onClick={handleClose}
          />
        </div>
        <div className="max-h-[50vh] lg:max-h-[548px] pr-6 overflow-y-auto custom-scrollbar-store">
          {children}
        </div>
      </div>
    </div>
  );
}

export default EMIDetailsModal;
